import { DecisionAction, DecisionActionCompact } from "../../models/home";
import { LanguageService } from "../language/language.service";
import { HomeDecisionI18nCore } from "./home-decision-detail-i18n.service.technical-and-fallbacks";

export type HomeDecisionActionLike = Pick<DecisionActionCompact, "actionKey" | "label"> &
  Partial<Pick<DecisionAction, "description">>;

export class HomeDecisionActionI18n {
  constructor(private readonly lang: LanguageService, private readonly core: HomeDecisionI18nCore) {}

  translateDecisionReason(reason?: string | null): string | null { return this.core.translateCopy(reason); }
  translateActionLabel(action: HomeDecisionActionLike | null | undefined): string | null {
    if (!action) return null;
    return this.core.translateByNormalizedKey(action.actionKey, "home.decisionDetail.action.label.") ??
      this.core.translateCopy(action.label) ?? action.label;
  }
  translateActionDescription(action: HomeDecisionActionLike | null | undefined): string | null {
    if (!action?.description) return null;
    return this.core.translateByNormalizedKey(action.actionKey, "home.decisionDetail.action.description.") ??
      this.core.translateCopy(action.description) ?? action.description;
  }
  translateActionSummaryLabel(summary: string | null | undefined, action: HomeDecisionActionLike | null | undefined): string {
    if (!summary) return this.lang.translate("home.decisionDetail.noActionAvailable");
    if (action?.label && summary === action.label) return this.translateActionLabel(action) ?? summary;
    return this.core.translateCopy(summary) ?? summary;
  }
  translateActionSummaryCopy(summary: string | null | undefined, action: HomeDecisionActionLike | null | undefined): string {
    if (!summary) return this.lang.translate("home.decisionActionUx.readyCopy");
    if (action?.description && summary === action.description) return this.translateActionDescription(action) ?? summary;
    return this.translateActionDisabledReason(summary) ?? this.core.translateCopy(summary) ?? summary;
  }
  translateActionCriterion(action: HomeDecisionActionLike | null | undefined, criterion: string | null | undefined, index: number): string {
    if (!criterion) return "";
    const actionKey = this.core.normalizeKey(action?.actionKey);
    return (actionKey ? this.core.translateKnownKey(`home.decisionDetail.action.criteria.${actionKey}.${index}`) : null) ??
      this.core.translateCopy(criterion) ?? criterion;
  }
  translateActionDisabledReason(reason?: string | null): string | null {
    if (!reason || !this.core.shouldTranslateResponses()) return reason ?? null;
    const match = /^Action is disabled in state '([^']+)'\.$/i.exec(reason);
    if (match) {
      const state = this.core.translateState(match[1]) ?? match[1];
      return this.core.interpolate("home.decisionDetail.dynamic.pattern.action_disabled_in_state", { state }) ?? reason;
    }
    return this.core.translateCopy(reason) ?? reason;
  }
  translateActionFieldOptionLabel(rawLabel?: string | null): string | null { return this.core.translateCopy(rawLabel) ?? rawLabel ?? null; }
  translateActionSubmitLabel(action: HomeDecisionActionLike | null | undefined): string {
    return this.translateActionLabel(action) ?? this.lang.translate("home.decisionActionForm.submit");
  }
  translateActionFormCopy(action: HomeDecisionActionLike | null | undefined): string | null {
    const key = this.core.normalizeKey(action?.actionKey);
    return (key ? this.core.translateKnownKey(`home.decisionDetail.action.${key}.formCopy`) : null) ??
      this.translateActionDescription(action);
  }
  translateActionConfirmationLabel(action: HomeDecisionActionLike | null | undefined): string {
    const key = this.core.normalizeKey(action?.actionKey);
    return (key ? this.core.translateKnownKey(`home.decisionDetail.action.${key}.confirmLabel`) : null) ??
      this.lang.translate("home.decisionActionForm.confirmExecution");
  }
}
